import {
  Bar,
  DatafeedConfiguration,
  DatafeedErrorCallback,
  GetMarksCallback,
  HistoryCallback,
  HistoryMetadata,
  IDatafeedChartApi,
  IDatafeedQuotesApi,
  IExternalDatafeed,
  LibrarySymbolInfo,
  Mark,
  OnReadyCallback,
  PeriodParams,
  QuoteData,
  QuotesCallback,
  ResolutionString,
  SearchSymbolResultItem,
  ServerTimeCallback,
  SubscribeBarsCallback,
  SymbolResolveExtension,
  TimescaleMark,
  VisiblePlotsSet,
} from '@frontends/feature-charting-library/datafeed-api';
import { supportedResolutions } from '@frontends/feature-tv-parts';
import { getSymbolIconUrl } from '@frontends/util-symbol-icons';
import { store } from '../../../app/store';
import { selectAccount, selectSymbolByName, selectSymbols } from '../../../features/initialSlice/initialSlice';
import {
  changeCurrentDate,
  changeSymbol,
  selectCurrentDate,
  selectMaxDate,
  selectStepRes,
  setChartSpeed,
  setIsFinishReached,
  setIsPaused,
  setPanelBlocked,
  triggerDateChange,
} from '../../../features/mainSlice/mainSlice';
import { $axios } from '../../../hooks/useAxiosInterceptors';
import { defaultConfig, TEntityConfig } from '../../../hooks/useEntityConfig';
import {
  adjustBarTime,
  clearLastBars,
  floorTo,
  getDateInSession,
  getErrorMessage,
  logMessage,
  resolutionToSeconds,
  UdfErrorResponse,
  UdfOkResponse,
  UdfResponse,
} from './helpers';
import { ICustomDatafeed } from './provider-interfaces';

// ─── Types ────────────────────────────────────────────────────────────────────

// UDF response arrays are parsed with parseFloat() below, so each element is stringifiable
type UdfNumberArray = (number | string)[];

interface HistoryPartialDataResponse extends UdfOkResponse {
  c: UdfNumberArray;
  t: UdfNumberArray;
  h?: never;
  l?: never;
  o?: never;
  v?: never;
}

interface HistoryFullDataResponse extends UdfOkResponse {
  c: UdfNumberArray;
  h: UdfNumberArray;
  l: UdfNumberArray;
  o: UdfNumberArray;
  t: UdfNumberArray;
  v: UdfNumberArray;
}

interface HistoryNoDataResponse extends UdfResponse {
  s: 'no_data';
  nextTime?: number;
}

type HistoryResponse = HistoryFullDataResponse | HistoryPartialDataResponse | HistoryNoDataResponse;

export type PeriodParamsWithOptionalCountback = Omit<PeriodParams, 'countBack'> & {
  countBack?: number;
};

export interface GetBarsResult {
  bars: Bar[];
  meta: HistoryMetadata;
}

export interface LimitedResponseConfiguration {
  expectedOrder: 'latestFirst' | 'earliestFirst';
  maxResponseLength: number;
}

export interface UdfCompatibleConfiguration extends DatafeedConfiguration {
  supports_group_request?: boolean;
  supports_search?: boolean;
}

export interface ResolveSymbolResponse extends LibrarySymbolInfo {
  'currency-code': string;
  'exchange-listed': string;
  'exchange-traded': string;
  'has-daily': boolean;
  'has-intraday': boolean;
  'intraday-multipliers': string[];
  minmovement: number;
  'original-currency-code': string;
  'original-unit-id': string;
  s: undefined;
  'session-holidays': string;
  'session-regular': string;
  'supported-resolutions': ResolutionString[];
  'unit-conversion-types': string[];
  'unit-id': string;
  'has-empty-bars'?: boolean;
  'has-weekly-and-monthly'?: boolean;
  minmovement2?: number;
  'visible-plots-set'?: VisiblePlotsSet;
  'volume-precision'?: number;
}

// it is hack to let's TypeScript make code flow analysis
export interface UdfSearchSymbolsResponse extends Array<SearchSymbolResultItem> {
  s?: undefined;
}

export type ResetData = (fromDate: string, toDate?: string) => void;
export type ReachedTimeframeEndCallback = () => void;

export const enum Constants {
  SearchItemsLimit = 30,
}

interface QuoteSubscriber {
  fastSymbols: string[];
  listener: QuotesCallback;
  symbols: string[];
}

interface QuoteSubscribers {
  [listenerId: string]: QuoteSubscriber;
}

const enum SymbolsType {
  General,
  Fast,
}

const SYMBOL_ICONS_BASE_URL = '/assets/icons';

interface DataSubscriber {
  lastBarTime: number | null;
  listener: SubscribeBarsCallback;
  resolution: ResolutionString;
  symbolInfo: LibrarySymbolInfo;
}

interface DataSubscribers {
  [guid: string]: DataSubscriber;
}

// ─── DataFeed ─────────────────────────────────────────────────────────────────

export class DataFeed implements IExternalDatafeed, IDatafeedQuotesApi, IDatafeedChartApi, ICustomDatafeed {
  private static instance: DataFeed | null = null;

  private readonly applicationConfiguration: TEntityConfig = defaultConfig;
  private _timerHandle: ReturnType<typeof setInterval> | null = null;
  private _updateFrequency = 1 * 1000;
  private configuration: DatafeedConfiguration = {
    supported_resolutions: supportedResolutions.filter((resolution) => resolution !== '1M'),
    exchanges: [],
    symbols_types: [],
    supports_marks: false,
    supports_timescale_marks: false,
    supports_time: false,
  };

  // Bar storage (from HistoryProvider)
  private barStorage: Bar[] = [];
  public lastGivenBarTime = 0;
  private quotes_map = new Map<string, Bar[]>();
  private _barStorageLock: Promise<void> | null = null;

  // Subscription state (from DataPulseProvider)
  private readonly _dataSubscribers: DataSubscribers = {};
  private readonly _quoteSubscribers: QuoteSubscribers = {};
  private _dataRequestsPending = 0;
  private _quotesRequestsPending = 0;

  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop default; replaced with the real callback once subscribeBars() registers it
  private fubu: () => void = () => {};

  protected constructor(applicationConfiguration: TEntityConfig = defaultConfig) {
    this.applicationConfiguration = applicationConfiguration;
  }

  static getInstance(applicationConfiguration: TEntityConfig = defaultConfig): DataFeed {
    if (!DataFeed.instance) {
      DataFeed.instance = new DataFeed(applicationConfiguration);
    }

    return DataFeed.instance;
  }

  // ─── Timer / playback ───────────────────────────────────────────────────────

  public pauseTimer(): void {
    if (this._timerHandle !== null) {
      clearInterval(this._timerHandle);
      this._timerHandle = null;
    }
  }

  public startTimer(): void {
    if (this._timerHandle === null) {
      this._timerHandle = setInterval(this.updateAllData.bind(this), this._updateFrequency);
    }
  }

  public changeFrequency(value: number): void {
    this._updateFrequency = (1.0 / value) * 1000;

    if (this._timerHandle !== null) {
      this.pauseTimer();
      this.startTimer();
    }
  }

  public updateAllData(): void {
    const stepRes = selectStepRes(store.getState());
    const nextDate = selectCurrentDate(store.getState()) + resolutionToSeconds[stepRes];
    store.dispatch(changeCurrentDate(nextDate));

    if (nextDate >= selectMaxDate(store.getState())) {
      this.pauseTimer();
      store.dispatch(setChartSpeed(1));
      store.dispatch(setIsPaused(true));
      store.dispatch(setPanelBlocked(false));
      store.dispatch(setIsFinishReached(true));
    }

    this._updateData();
    this._updateQuotes(SymbolsType.Fast);
  }

  // ─── Bar storage ────────────────────────────────────────────────────────────

  public clearBarStorage(): void {
    this.barStorage.length = 0;
  }

  public getSecFromResolution(res: string): number {
    return resolutionToSeconds[res];
  }

  private async _fetchBars(params: { symbol: string; from: number; to: number; resolution: string }): Promise<Bar[]> {
    const { symbol, from, to, resolution } = params;
    const query = `timeframe=${resolutionToSeconds[resolution] / 60}&from_time=${from}&to_time=${to}`;

    const tradingAccountId = selectAccount(store.getState())?.login;

    if (!tradingAccountId) return [];

    try {
      const response = await $axios.get(
        `${this.applicationConfiguration.VITE_URL_CHART_SERVICE}/api/v1/trading/accounts/${tradingAccountId}/symbols/${encodeURIComponent(symbol)}/charts?${query}`,
      );
      console.log(response.data);

      return (await response.data) as Bar[];
    } catch {
      return [];
    }
  }

  private async _getBarsAsync(
    symbolInfo: LibrarySymbolInfo,
    resolution: string,
    periodParams: PeriodParamsWithOptionalCountback,
  ): Promise<GetBarsResult> {
    console.log('GETBARS_getBarsAsync_start', symbolInfo.session, 'ASKED FOR', periodParams.from, periodParams.to);

    const stepRes = selectStepRes(store.getState());
    const stepSec = resolutionToSeconds[stepRes];
    const resSec = resolutionToSeconds[resolution];
    const params = {
      symbol: symbolInfo.name,
      from: adjustBarTime(periodParams.from * 1000, stepSec, resSec) / 1000,
      to: floorTo(periodParams.to, stepSec) + stepSec * 200 + (stepSec - 1),
      resolution: stepRes,
    };

    console.log('GETBARS_getBarsAsync_processed', symbolInfo.session, 'ASKED FOR', params.from, params.to);

    try {
      if (periodParams.from > selectCurrentDate(store.getState())) {
        return { bars: [], meta: { noData: true } };
      }

      while (this._barStorageLock) {
        await this._barStorageLock;
      }

      let resolveBarLock!: () => void;
      this._barStorageLock = new Promise<void>((resolve) => {
        resolveBarLock = resolve;
      });

      try {
        if (this.barStorage.length === 0) {
          this.barStorage = await this._fetchBars(params);
        } else {
          if (periodParams.from * 1000 < this.barStorage[0].time) {
            const paramsAdjusted = {
              ...params,
              to: Math.floor(this.barStorage[0].time / 1000) - 1,
            };
            this.barStorage = (await this._fetchBars(paramsAdjusted)).concat(this.barStorage);
          }

          if (periodParams.to * 1000 > this.barStorage[this.barStorage.length - 1].time) {
            const paramsAdjusted = {
              ...params,
              from: Math.floor(this.barStorage[this.barStorage.length - 1].time / 1000) + stepSec,
            };
            this.barStorage = this.barStorage.concat(await this._fetchBars(paramsAdjusted));
          }
        }
      } finally {
        this._barStorageLock = null;
        resolveBarLock();
      }

      const fromMs = adjustBarTime(periodParams.from * 1000, stepSec, resSec);
      const toMs = adjustBarTime(periodParams.to * 1000, stepSec, resSec) + resSec * 1000 - 1;
      const returnBars: Bar[] = [];

      for (const bar of this.barStorage) {
        const localBar = { ...bar };

        if (localBar.time < fromMs) continue;

        if (localBar.time > toMs) break;

        const lastBar = returnBars[returnBars.length - 1];

        if (lastBar && localBar.time <= lastBar.time) {
          console.log('Skipping bar ', localBar.time, ' because it is older than the last returned bar ', lastBar.time);

          continue;
        }

        if (!lastBar || localBar.time >= lastBar.time + resSec * 1000) {
          localBar.time = adjustBarTime(localBar.time, stepSec, resSec);
          returnBars.push(localBar);
        } else {
          lastBar.close = localBar.close;
          lastBar.high = Math.max(localBar.high, lastBar.high);
          lastBar.low = Math.min(localBar.low, lastBar.low);

          if (lastBar.volume && localBar.volume) {
            lastBar.volume += localBar.volume;
          }
        }
      }

      const noDataBool = returnBars.length === 0;

      if (returnBars.length > 1 && periodParams.to >= selectCurrentDate(store.getState())) {
        console.log('Updated map');

        this.quotes_map.set(symbolInfo.name, returnBars.slice(-2));
      }

      console.log('LAST BAR RETURNED with date requested', params.from, params.to, resolution, returnBars);
      console.log('LAST BAR IN STORAGE', returnBars[returnBars.length - 1]);

      return { bars: returnBars, meta: { noData: noDataBool } };
    } catch (error: unknown) {
      if (error instanceof Error || typeof error === 'string') {
        const reasonString = getErrorMessage(error);
        console.warn(`DataFeed: getBars() failed, error=${reasonString}`);

        throw reasonString;
      }

      throw error;
    }
  }

  public _processHistoryResponse(response: HistoryResponse | UdfErrorResponse): GetBarsResult {
    if (response.s !== 'ok' && response.s !== 'no_data') {
      throw new Error(response.errmsg);
    }

    const bars: Bar[] = [];
    const meta: HistoryMetadata = { noData: false };

    if (response.s === 'no_data') {
      meta.noData = true;
      meta.nextTime = response.nextTime;
    } else {
      const volumePresent = response.v !== undefined;
      const ohlPresent = response.o !== undefined;

      for (let i = 0; i < response.t.length; ++i) {
        const barValue: Bar = {
          time: response.t[i] * 1000,
          close: parseFloat(response.c[i]),
          open: parseFloat(response.c[i]),
          high: parseFloat(response.c[i]),
          low: parseFloat(response.c[i]),
        };

        if (ohlPresent) {
          barValue.open = parseFloat((response as HistoryFullDataResponse).o[i]);
          barValue.high = parseFloat((response as HistoryFullDataResponse).h[i]);
          barValue.low = parseFloat((response as HistoryFullDataResponse).l[i]);
        }

        if (volumePresent) {
          barValue.volume = parseFloat((response as HistoryFullDataResponse).v[i]);
        }

        bars.push(barValue);
      }
    }

    return { bars, meta };
  }

  // ─── Quotes ─────────────────────────────────────────────────────────────────

  private _computeQuoteData(symbol: string): QuoteData {
    const lastBars = this.quotes_map.get(symbol);

    if (lastBars) {
      const lp = lastBars[1].close;
      const openPrice = lastBars[1].open;
      const highPrice = lastBars[1].high;
      const lowPrice = lastBars[1].low;
      const prevClosePrice = lastBars[0].close;
      const ch = lp - (prevClosePrice ?? lp);
      const chp = (prevClosePrice ?? lp) !== 0 ? (ch / (prevClosePrice ?? lp)) * 100 : 0;
      const tick = lp !== 0 ? Math.max(Math.abs(lp) * 0.00005, 0.00001) : 0.00001;
      const bid = lp - tick;
      const ask = lp + tick;
      const spread = ask - bid;
      const lastLowPrice = lastBars[0].low;
      const lastHighPrice = lastBars[0].high;

      return {
        n: symbol,
        s: 'ok',
        v: {
          lp,
          open_price: openPrice,
          high_price: highPrice,
          low_price: lowPrice,
          prev_close_price: prevClosePrice,
          ch,
          chp,
          bid,
          ask,
          spread,
          volume: 1,
          last_low_price: lastLowPrice,
          last_high_price: lastHighPrice,
        },
      };
    }

    return {
      n: symbol,
      s: 'ok',
      v: {
        lp: 0,
        open_price: 0,
        high_price: 0,
        low_price: 0,
        prev_close_price: 0,
        ch: 0,
        chp: 0,
        bid: 0,
        ask: 0,
        spread: 0,
        volume: 0,
        last_low_price: 0,
        last_high_price: 0,
      },
    };
  }

  private _getQuotesData(symbols: string[]): QuoteData[] {
    return symbols.map((symbol) => this._computeQuoteData(symbol));
  }

  // ─── Subscription internals (DataPulseProvider) ──────────────────────────────

  private _updateData(): void {
    if (this._dataRequestsPending > 0) {
      return;
    }

    this._dataRequestsPending = 0;

    for (const listenerGuid in this._dataSubscribers) {
      this._dataRequestsPending += 1;
      this._updateDataForSubscriber(listenerGuid)
        .then(() => {
          this._dataRequestsPending -= 1;
          logMessage(`DataFeed: data for #${listenerGuid} updated successfully, pending=${this._dataRequestsPending}`);
        })
        .catch((reason?: string | Error) => {
          this._dataRequestsPending -= 1;
          logMessage(
            `DataFeed: data for #${listenerGuid} updated with error=${getErrorMessage(reason)}, pending=${this._dataRequestsPending}`,
          );
        });
    }
  }

  private _updateDataForSubscriber(listenerGuid: string): Promise<void> {
    const subscriptionRecord = this._dataSubscribers[listenerGuid];
    const rangeEndTime = selectCurrentDate(store.getState());
    const defaultStart = rangeEndTime - periodLengthSeconds(subscriptionRecord.resolution, 4);

    const lastBarTimeSec =
      subscriptionRecord.lastBarTime !== null ? Math.floor(subscriptionRecord.lastBarTime / 1000) : rangeEndTime;
    const rangeStartTime = Math.min(defaultStart, lastBarTimeSec);

    return this._getBarsAsync(subscriptionRecord.symbolInfo, subscriptionRecord.resolution as ResolutionString, {
      from: rangeStartTime,
      to: rangeEndTime,
      countBack: 2,
      firstDataRequest: false,
    }).then((result: GetBarsResult) => {
      this._onSubscriberDataReceived(listenerGuid, result);
      this._updateQuotes(SymbolsType.Fast);
    });
  }

  private _onSubscriberDataReceived(listenerGuid: string, result: GetBarsResult): void {
    if (!Object.hasOwn(this._dataSubscribers, listenerGuid)) {
      logMessage(`DataFeed: Data comes for already unsubscribed subscription #${listenerGuid}`);

      return;
    }

    const bars = result.bars;

    if (bars.length === 0) return;

    const lastBar = bars[bars.length - 1];
    const subscriptionRecord = this._dataSubscribers[listenerGuid];

    if (subscriptionRecord.lastBarTime !== null && lastBar.time < subscriptionRecord.lastBarTime) {
      return;
    }

    const lastBarTime = subscriptionRecord.lastBarTime;
    const isNewBar = lastBarTime !== null && lastBar.time > lastBarTime;

    if (isNewBar) {
      const firstNewIdx = bars.findIndex((bar) => bar.time > lastBarTime);

      if (firstNewIdx >= 0) {
        for (const bar of bars.slice(firstNewIdx)) {
          subscriptionRecord.listener(bar);
        }
      }
    } else {
      subscriptionRecord.listener(lastBar);
    }

    subscriptionRecord.lastBarTime = lastBar.time;
  }

  private _updateQuotes(updateType: SymbolsType): void {
    if (this._quotesRequestsPending > 0) return;

    for (const listenerGuid in this._quoteSubscribers) {
      this._quotesRequestsPending++;
      const subscriptionRecord = this._quoteSubscribers[listenerGuid];
      const symbols = updateType === SymbolsType.Fast ? subscriptionRecord.fastSymbols : subscriptionRecord.symbols;
      const data = this._getQuotesData(symbols);
      this._quotesRequestsPending--;

      if (!Object.hasOwn(this._quoteSubscribers, listenerGuid)) return;

      subscriptionRecord.listener(data);
    }
  }

  onReady(callback: OnReadyCallback): void {
    setTimeout(() => callback(this.configuration), 0);
  }

  private getSymbols(): LibrarySymbolInfo[] {
    return selectSymbols(store.getState());
  }

  private getSymbolByName(name: string): LibrarySymbolInfo | null {
    return selectSymbolByName(name)(store.getState());
  }

  resolveSymbol(
    symbolId: string,
    onResolve: (symbolInfo: LibrarySymbolInfo) => void,
    onError: (err: string) => void,
    _extension?: SymbolResolveExtension,
  ): void {
    const rawSymbol = this.getSymbolByName(symbolId);

    if (!rawSymbol) {
      setTimeout(() => onError(`Symbol not found: ${symbolId}`), 0);

      return;
    }

    const symbol: LibrarySymbolInfo = {
      ...rawSymbol,
      supported_resolutions: rawSymbol.supported_resolutions ? [...rawSymbol.supported_resolutions] : undefined,
      logo_urls: getLogoUrls(rawSymbol),
      library_custom_fields: rawSymbol.library_custom_fields ? { ...rawSymbol.library_custom_fields } : undefined,
    };
    localStorage.setItem('lastSelectedSymbol', symbol.name);
    setTimeout(() => onResolve(symbol), 0);
    this.clearBarStorage();
    store.dispatch(changeSymbol({ ...symbol }));
    store.dispatch(setIsPaused(true));
    store.dispatch(triggerDateChange());
  }

  searchSymbols(
    userInput: string,
    exchange: string,
    symbolType: string,
    onResult: (items: SearchSymbolResultItem[]) => void,
  ): void {
    const symbols = this.getSymbols()
      .filter((symbol) => {
        const matchesName =
          symbol.name.toLowerCase().includes(userInput.toLowerCase()) ||
          symbol.description.toLowerCase().includes(userInput.toLowerCase());
        const matchesType = symbolType === '' || symbol.type === symbolType;

        return matchesName && matchesType;
      })
      .map((symbol) => ({
        symbol: symbol.name,
        description: symbol.description,
        exchange: symbol.exchange,
        ticker: symbol.ticker,
        type: symbol.type,
        logo_urls: getLogoUrls(symbol),
        exchange_logo: symbol.exchange_logo,
      }));
    setTimeout(() => onResult(symbols), 0);
  }

  public getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParamsWithOptionalCountback,
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback,
  ): void {
    const currentDate = selectCurrentDate(store.getState());
    const resSec = resolutionToSeconds[resolution];
    const toDate = periodParams.to > currentDate ? currentDate : periodParams.to;
    const fromDate =
      periodParams.from > toDate
        ? getDateInSession(symbolInfo, toDate) - resSec * 10
        : periodParams.to > currentDate
          ? periodParams.from - resSec * 10
          : periodParams.from;

    periodParams.from = fromDate;
    periodParams.to = toDate;

    this._getBarsAsync(symbolInfo, resolution, periodParams)
      .then((result: GetBarsResult) => {
        onResult(result.bars, result.meta);
        this._updateQuotes(SymbolsType.Fast);
      })
      .catch(onError);
  }

  public subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
    _onResetCacheNeededCallback: () => void,
  ): void {
    this.fubu = _onResetCacheNeededCallback;

    if (Object.hasOwn(this._dataSubscribers, listenerGuid)) {
      logMessage(`DataFeed: already has subscriber with id=${listenerGuid}`);

      return;
    }

    this._dataSubscribers[listenerGuid] = {
      lastBarTime: null,
      listener: onTick,
      resolution: resolution,
      symbolInfo: symbolInfo,
    };
    logMessage(`DataFeed: subscribed for #${listenerGuid} - {${symbolInfo.name}, ${resolution}}`);
  }

  public unsubscribeBars(listenerGuid: string): void {
    delete this._dataSubscribers[listenerGuid];
    logMessage(`DataFeed: unsubscribed for #${listenerGuid}`);
  }

  public getQuotes(symbols: string[], onDataCallback: QuotesCallback, onErrorCallback: (msg: string) => void): void {
    try {
      onDataCallback(this._getQuotesData(symbols));
    } catch {
      onErrorCallback('Datafeed error. get quotes');
    }
  }

  public subscribeQuotes(
    symbols: string[],
    fastSymbols: string[],
    onRealtimeCallback: QuotesCallback,
    listenerGuid: string,
  ): void {
    this._quoteSubscribers[listenerGuid] = { symbols, fastSymbols, listener: onRealtimeCallback };
    logMessage(`DataFeed: subscribed quotes with #${listenerGuid}`);
  }

  public unsubscribeQuotes(listenerGuid: string): void {
    delete this._quoteSubscribers[listenerGuid];
    logMessage(`DataFeed: unsubscribed quotes with #${listenerGuid}`);
  }

  public getMarks(
    _symbolInfo: LibrarySymbolInfo,
    _from: number,
    _to: number,
    _onDataCallback: GetMarksCallback<Mark>,
    _resolution: ResolutionString,
  ): void {
    if (!this.configuration.supports_marks) return;
  }

  public getTimescaleMarks(
    _symbolInfo: LibrarySymbolInfo,
    _from: number,
    _to: number,
    _onDataCallback: GetMarksCallback<TimescaleMark>,
    _resolution: ResolutionString,
  ): void {
    if (!this.configuration.supports_timescale_marks) return;
  }

  public getServerTime(_callback: ServerTimeCallback): void {
    if (!this.configuration.supports_time) return;
  }

  public resetChartData(): void {
    this.fubu();
    clearLastBars();
  }

  // kept for external callers
  public triggerQuotesUpdate(): void {
    this._updateQuotes(SymbolsType.Fast);
  }
}

function periodLengthSeconds(resolution: string, requiredPeriodsCount: number): number {
  let daysCount: number;

  if (resolution === 'D' || resolution === '1D') {
    daysCount = requiredPeriodsCount;
  } else if (resolution === 'M' || resolution === '1M') {
    daysCount = 31 * requiredPeriodsCount;
  } else if (resolution === 'W' || resolution === '1W') {
    daysCount = 7 * requiredPeriodsCount;
  } else {
    daysCount = (requiredPeriodsCount * parseInt(resolution)) / (24 * 60);
  }

  return daysCount * 24 * 60 * 60;
}

function getLogoUrls(symbol: LibrarySymbolInfo): LibrarySymbolInfo['logo_urls'] {
  const fields = symbol.library_custom_fields ?? {};
  const currencyBase = fields.currency_base;
  const currencyProfit = fields.currency_profit;

  if (typeof currencyBase === 'string' && typeof currencyProfit === 'string' && currencyBase !== currencyProfit) {
    return [
      getSymbolIconUrl(SYMBOL_ICONS_BASE_URL, currencyBase),
      getSymbolIconUrl(SYMBOL_ICONS_BASE_URL, currencyProfit),
    ];
  }

  return [getSymbolIconUrl(SYMBOL_ICONS_BASE_URL, symbol.name)];
}
